import { useState, useRef, useCallback } from 'react';
import { API_BASE_URL } from '@/services/api/client';
import { TokenService } from '@/services/auth/TokenService';
import { readSSEStream } from '@/utils/sseStream';
import {
  auditStoryActivities,
  auditStoryAudioScript,
  type ActivityIntegrityError,
} from '@/services/api/backoffice.api';

export type GenerationStatus = 'idle' | 'generating' | 'complete' | 'error';

export interface GeneratedStory {
  title: string;
  description: string;
  titleTranslation?: string;
  descriptionTranslation?: string;
  level: string;
  category: string;
  type: string;
  estimatedTime: number;
  xpReward: number;
  status: string;
  resources: Array<{
    type: string;
    title: string;
    content: string;
    order: number;
  }>;
  activities: Array<Record<string, unknown>>;
  learningObjectives?: Array<{ id: string; canStatement: string; skill: string }>;
}

export function useGenerateStory() {
  const [status, setStatus] = useState<GenerationStatus>('idle');
  const [progressMessages, setProgressMessages] = useState<string[]>([]);
  const [generatedStory, setGeneratedStory] = useState<GeneratedStory | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [integrityErrors, setIntegrityErrors] = useState<ActivityIntegrityError[]>([]);
  const [auditingActivities, setAuditingActivities] = useState(false);
  const [audioScriptErrors, setAudioScriptErrors] = useState<string[]>([]);
  const [auditingAudioScript, setAuditingAudioScript] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const generate = useCallback(async (topic: string, level: string, category: string) => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    setStatus('generating');
    setProgressMessages([]);
    setGeneratedStory(null);
    setError(null);
    setIntegrityErrors([]);
    setAudioScriptErrors([]);

    const token = TokenService.getToken();

    try {
      const response = await fetch(`${API_BASE_URL}/backoffice/generate-story`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ topic, level, category }),
        signal: abortRef.current.signal,
      });

      if (!response.ok) {
        const data = await response.json() as { error?: string };
        throw new Error(data.error ?? `Error ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response stream available');

      await readSSEStream(reader, (event) => {
        if (event.type === 'progress' && event.message) {
          setProgressMessages((prev) => [...prev, event.message!]);
        } else if (event.type === 'integrity_warning' && Array.isArray(event.errors)) {
          setIntegrityErrors(event.errors as ActivityIntegrityError[]);
        } else if (event.type === 'audio_script_warning' && Array.isArray(event.errors)) {
          setAudioScriptErrors(event.errors as string[]);
        } else if (event.type === 'complete' && event.story) {
          setGeneratedStory(event.story as GeneratedStory);
          setStatus('complete');
        } else if (event.type === 'error') {
          throw new Error(event.message ?? 'Error desconocido');
        }
      });
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      const message = err instanceof Error ? err.message : 'Error desconocido';
      setError(message);
      setStatus('error');
    }
  }, []);

  /**
   * Pasada correctiva on-demand. Mantiene el draft, sólo sustituye las
   * actividades por la versión que devuelve el auditor del backend. Si quedan
   * errores residuales, se exponen para que el modal vuelva a mostrarlos.
   */
  const retryIntegrityWithAI = useCallback(async () => {
    if (!generatedStory) return;
    setAuditingActivities(true);
    try {
      const { activities, errors } = await auditStoryActivities(generatedStory.activities);
      setGeneratedStory({ ...generatedStory, activities });
      setIntegrityErrors(errors);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error desconocido';
      setIntegrityErrors((prev) => [
        { index: -1, errors: [`El reintento con IA falló: ${message}`] },
        ...prev.filter((e) => e.index !== -1),
      ]);
    } finally {
      setAuditingActivities(false);
    }
  }, [generatedStory]);

  /**
   * Permite a la página inyectar los errores devueltos por el endpoint de
   * guardado cuando responde 400 con `details` (mismo formato que el SSE
   * `integrity_warning`).
   */
  const setIntegrityErrorsFromSave = useCallback((errors: ActivityIntegrityError[]) => {
    setIntegrityErrors(errors);
  }, []);

  const clearIntegrityErrors = useCallback(() => setIntegrityErrors([]), []);

  /**
   * Pasada correctiva on-demand del Audio Script. Sustituye `content` del
   * recurso "Audio Script" por la versión reformateada que devuelve el
   * backend. Si el reformatter no consigue arreglarlo, los errores residuales
   * se reexponen para que el modal vuelva a mostrarlos y el editor pueda
   * volver a intentarlo.
   */
  const retryAudioScriptWithAI = useCallback(async () => {
    if (!generatedStory) return;
    const idx = generatedStory.resources.findIndex((r) => r.title === 'Audio Script');
    if (idx === -1) return;
    const current = generatedStory.resources[idx]!;
    setAuditingAudioScript(true);
    try {
      const { content, errors } = await auditStoryAudioScript(current.content);
      const nextResources = generatedStory.resources.map((r, i) =>
        i === idx ? { ...r, content } : r,
      );
      setGeneratedStory({ ...generatedStory, resources: nextResources });
      setAudioScriptErrors(errors);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error desconocido';
      setAudioScriptErrors([`El reintento con IA falló: ${message}`]);
    } finally {
      setAuditingAudioScript(false);
    }
  }, [generatedStory]);

  const clearAudioScriptErrors = useCallback(() => setAudioScriptErrors([]), []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setStatus('idle');
    setProgressMessages([]);
    setGeneratedStory(null);
    setError(null);
    setIntegrityErrors([]);
    setAuditingActivities(false);
    setAudioScriptErrors([]);
    setAuditingAudioScript(false);
  }, []);

  return {
    status,
    progressMessages,
    generatedStory,
    error,
    integrityErrors,
    auditingActivities,
    audioScriptErrors,
    auditingAudioScript,
    generate,
    retryIntegrityWithAI,
    retryAudioScriptWithAI,
    setIntegrityErrorsFromSave,
    clearIntegrityErrors,
    clearAudioScriptErrors,
    reset,
  };
}
